import React, { useState, useRef, useEffect, useCallback } from 'react';

async function apiSavePositions(doorPos: Record<string,any>, camPos: Record<string,any>) {
  const token = localStorage.getItem('lemai_token');
  if (!token) return;
  try {
    // Also mirror to localStorage as fallback
    localStorage.setItem('siteMapDoorPositions', JSON.stringify(doorPos));
    localStorage.setItem('siteMapCameraPositions', JSON.stringify(camPos));
    await fetch('/api/sitemap/positions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ doorPositions: doorPos, cameraPositions: camPos }),
    });
  } catch {}
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type MapMode = 'soc' | 'video' | 'access' | 'vision';

export interface MapCamera {
  id: string; name: string; zone: string;
  status: 'online' | 'offline' | 'motion' | 'alarm';
  cx: number; cy: number;
}

export interface MapDoor {
  id: string; name: string; zone: string;
  status: 'locked' | 'unlocked' | 'open' | 'forced' | 'tamper';
  x: number; y: number; horizontal: boolean;
}

export interface MapOutput {
  id: string; name: string; zone: string;
  status: 'active' | 'inactive' | 'fault';
  cx: number; cy: number;
}

export interface MapZone {
  id: string; name: string;
  status: 'normal' | 'alarm' | 'isolated' | 'tamper';
  x: number; y: number; w: number; h: number;
}

export interface MapAlarm {
  id: string | number; type: string; zone: string; severity: string;
}

interface BldZone {
  id: string; label: string; sub?: string;
  x: number; y: number; w: number; h: number;
  occupancy: number;
}

// ─── Building layout (shared across all modes) ────────────────────────────────

const BLDG: BldZone[] = [
  { id:'office-a',  label:'Office Zone A',  x:30,  y:30,  w:280, h:185, occupancy:72 },
  { id:'office-b',  label:'Office Zone B',  x:310, y:30,  w:250, h:185, occupancy:58 },
  { id:'server',    label:'Server Room',    x:560, y:30,  w:230, h:100, occupancy:18 },
  { id:'meeting',   label:'IT / Meeting',   x:560, y:130, w:230, h:85,  occupancy:44 },
  { id:'lobby',     label:'Lobby',          sub:'Reception', x:30, y:215, w:220, h:155, occupancy:55 },
  { id:'corridor',  label:'Corridor',       x:250, y:215, w:310, h:80,  occupancy:30 },
  { id:'control',   label:'Control Room',   x:560, y:215, w:230, h:155, occupancy:10 },
  { id:'carpark',   label:'Car Park',       sub:'External · Level B1/B2', x:30, y:390, w:760, h:90, occupancy:42 },
];

export const DEFAULT_CAMERAS: MapCamera[] = [
  { id:'CAM-01', name:'Main Entry',      zone:'lobby',    status:'online', cx:140, cy:368 },
  { id:'CAM-02', name:'Lobby Interior',  zone:'lobby',    status:'motion', cx:130, cy:270 },
  { id:'CAM-03', name:'Office A North',  zone:'office-a', status:'online', cx:175, cy:118 },
  { id:'CAM-04', name:'Office B Wide',   zone:'office-b', status:'online', cx:435, cy:118 },
  { id:'CAM-05', name:'Server Corridor', zone:'server',   status:'online', cx:680, cy:78 },
  { id:'CAM-06', name:'IT Meeting',      zone:'meeting',  status:'offline',cx:680, cy:173 },
  { id:'CAM-07', name:'Control Room',    zone:'control',  status:'online', cx:680, cy:300 },
  { id:'CAM-08', name:'Car Park Entry',  zone:'carpark',  status:'online', cx:155, cy:435 },
  { id:'CAM-09', name:'Car Park Exit',   zone:'carpark',  status:'online', cx:620, cy:435 },
  { id:'CAM-10', name:'Corridor Wide',   zone:'corridor', status:'alarm',  cx:405, cy:253 },
];

export const DEFAULT_DOORS: MapDoor[] = [
  { id:'DOOR-01', name:'Main Entry',       zone:'lobby',    status:'unlocked', x:140, y:376, horizontal:true  },
  { id:'DOOR-02', name:'Server Room',      zone:'server',   status:'locked',   x:561, y:79,  horizontal:false },
  { id:'DOOR-03', name:'Lobby East',       zone:'lobby',    status:'unlocked', x:250, y:268, horizontal:false },
  { id:'DOOR-04', name:'Control Room',     zone:'control',  status:'locked',   x:678, y:215, horizontal:false },
  { id:'DOOR-05', name:'Car Park Barrier', zone:'carpark',  status:'unlocked', x:405, y:381, horizontal:true  },
  { id:'DOOR-06', name:'Stairwell A',      zone:'office-a', status:'locked',   x:170, y:215, horizontal:false },
];

export const DEFAULT_OUTPUTS: MapOutput[] = [
  { id:'OUT-01', name:'Siren',        zone:'lobby',    status:'inactive', cx:90,  cy:240 },
  { id:'OUT-02', name:'Strobe',       zone:'lobby',    status:'inactive', cx:200, cy:240 },
  { id:'OUT-03', name:'Maglock A',    zone:'server',   status:'active',   cx:645, cy:55  },
  { id:'OUT-04', name:'Maglock B',    zone:'control',  status:'active',   cx:645, cy:290 },
  { id:'OUT-05', name:'Boom Gate',    zone:'carpark',  status:'inactive', cx:405, cy:410 },
];

export const DEFAULT_ALARM_ZONES: MapZone[] = [
  { id:'AZ-01', name:'Office A Zone',  status:'normal', x:40,  y:40,  w:260, h:165 },
  { id:'AZ-02', name:'Office B Zone',  status:'normal', x:320, y:40,  w:230, h:165 },
  { id:'AZ-03', name:'Server Zone',    status:'normal', x:570, y:40,  w:210, h:90  },
  { id:'AZ-04', name:'Lobby Zone',     status:'normal', x:40,  y:225, w:200, h:135 },
  { id:'AZ-05', name:'Control Zone',   status:'normal', x:570, y:225, w:210, h:135 },
  { id:'AZ-06', name:'Car Park Zone',  status:'normal', x:40,  y:400, w:740, h:70  },
];

// ─── Color maps ───────────────────────────────────────────────────────────────

const CAM_C: Record<MapCamera['status'], string> = {
  online:'#22c55e', offline:'#ef4444', motion:'#f59e0b', alarm:'#ef4444',
};
const DOOR_C: Record<MapDoor['status'], string> = {
  locked:'#555555', unlocked:'#22c55e', open:'#3b82f6', forced:'#ef4444', tamper:'#ef4444',
};
const OUT_C: Record<MapOutput['status'], string> = {
  active:'#f59e0b', inactive:'#555555', fault:'#ef4444',
};
const ZONE_C: Record<MapZone['status'], { fill: string; stroke: string }> = {
  normal:   { fill:'rgba(59,130,246,0.10)',  stroke:'rgba(59,130,246,0.45)'  },
  alarm:    { fill:'rgba(239,68,68,0.15)',   stroke:'#ef4444'                },
  isolated: { fill:'rgba(245,158,11,0.12)',  stroke:'rgba(245,158,11,0.5)'  },
  tamper:   { fill:'rgba(239,68,68,0.10)',   stroke:'rgba(239,68,68,0.6)'   },
};
const SEV_C: Record<string, string> = {
  critical:'#ef4444', high:'#f59e0b', medium:'#3b82f6', low:'#555555',
};

function zoneHeat(occ: number): string {
  if (occ < 35) return `rgba(59,130,246,${(0.06 + occ / 350).toFixed(2)})`;
  if (occ < 65) return `rgba(245,158,11,${(0.10 + (occ - 35) / 250).toFixed(2)})`;
  return `rgba(239,68,68,${(0.14 + (occ - 65) / 250).toFixed(2)})`;
}

// ─── SiteMap ──────────────────────────────────────────────────────────────────

interface DoorPositions { [id: string]: { x: number; y: number } }

interface SiteMapProps {
  mode: MapMode;
  cameras?: MapCamera[];
  doors?: MapDoor[];
  outputs?: MapOutput[];
  alarmZones?: MapZone[];
  alarms?: MapAlarm[];
  onCameraClick?: (cam: MapCamera) => void;
  onDoorClick?: (door: MapDoor) => void;
  onDoorControl?: (id: string, cmd: 'lock'|'unlock'|'open') => void;
  onOutputClick?: (output: MapOutput) => void;
  onZoneClick?: (zone: MapZone) => void;
  onPositionChange?: (id: string, x: number, y: number) => void;
  onRemoveDoor?: (id: string) => void;
  onCameraPositionChange?: (id: string, x: number, y: number) => void;
  onRemoveCamera?: (id: string) => void;
  liveDoorStatus?: Record<string, MapDoor['status']>;
  liveZoneStatus?: Record<string, MapZone['status']>;
  height?: number;
  editLayout?: boolean;
  bgImage?: string;
  showZones?: boolean;
}

export default function SiteMap({
  mode,
  cameras = DEFAULT_CAMERAS,
  doors = DEFAULT_DOORS,
  outputs = [],
  alarmZones = [],
  alarms = [],
  onCameraClick,
  onDoorClick,
  onDoorControl,
  onOutputClick,
  onZoneClick,
  onPositionChange,
  onRemoveDoor,
  onCameraPositionChange,
  onRemoveCamera,
  liveDoorStatus,
  liveZoneStatus,
  height = 430,
  editLayout = false,
  bgImage,
  showZones = true,
}: SiteMapProps) {
  const [sel, setSel] = useState<string | null>(null);
  const [tip, setTip] = useState<{ bx: number; by: number; node: React.ReactNode } | null>(null);
  const [positions, setPositions] = useState<DoorPositions>(() => {
    try { return JSON.parse(localStorage.getItem('siteMapDoorPositions') || '{}'); } catch { return {}; }
  });
  const [camPositions, setCamPositions] = useState<Record<string,{x:number;y:number}>>(() => {
    try { return JSON.parse(localStorage.getItem('siteMapCameraPositions') || '{}'); } catch { return {}; }
  });
  const [dragging, setDragging] = useState<{ id: string; type: 'door'|'camera'; ox: number; oy: number; moved: boolean } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Load positions from backend on mount (falls back to localStorage)
  useEffect(() => {
    const token = localStorage.getItem('lemai_token');
    if (!token) return;
    fetch('/api/sitemap/positions', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.doorPositions && Object.keys(data.doorPositions).length > 0) {
          setPositions(data.doorPositions);
        }
        if (data?.cameraPositions && Object.keys(data.cameraPositions).length > 0) {
          setCamPositions(data.cameraPositions);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const VW = 820, VH = 500;

  function svgCoords(e: React.MouseEvent): { x: number; y: number } {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * VW,
      y: ((e.clientY - rect.top) / rect.height) * VH,
    };
  }

  function getDoorPos(door: MapDoor): { x: number; y: number } {
    return positions[door.id] ?? { x: door.x, y: door.y };
  }

  function getCamPos(cam: MapCamera): { x: number; y: number } {
    return camPositions[cam.id] ?? { x: cam.cx, y: cam.cy };
  }

  const alarmZoneIds = new Set(alarms.map(a => a.zone));

  function pickOutput(out: MapOutput, e: React.MouseEvent) {
    e.stopPropagation();
    const next = sel === out.id ? null : out.id;
    setSel(next);
    if (next) {
      const c = OUT_C[out.status];
      setTip({
        bx: out.cx, by: out.cy,
        node: (
          <>
            <div style={{ fontWeight:700, color:'white', fontSize:'12px', marginBottom:'4px' }}>{out.id} — {out.name}</div>
            <div style={{ fontSize:'11px', color:'#a0a0a0', marginBottom:'2px' }}>Zone: {out.zone}</div>
            <div style={{ fontSize:'11px' }}>Status: <span style={{ color:c, fontWeight:700, textTransform:'capitalize' }}>{out.status}</span></div>
            {mode === 'access' && (
              <div style={{ marginTop:'8px', display:'flex', gap:'5px' }}>
                {(['active','inactive'] as const).map(cmd => (
                  <div key={cmd} onClick={ev => { ev.stopPropagation(); onOutputClick?.(out); setTip(null); setSel(null); }}
                    style={{ padding:'3px 9px', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'5px', fontSize:'10px', color:'#f59e0b', cursor:'pointer', fontWeight:700, textTransform:'capitalize' }}>
                    {cmd === 'active' ? 'Activate' : 'Deactivate'}
                  </div>
                ))}
              </div>
            )}
          </>
        ),
      });
    } else {
      setTip(null);
    }
  }

  function pickAlarmZone(z: MapZone, e: React.MouseEvent) {
    e.stopPropagation();
    const next = sel === z.id ? null : z.id;
    setSel(next);
    if (next) {
      const effectiveStatus = (liveZoneStatus?.[z.id] ?? z.status) as MapZone['status'];
      const col = ZONE_C[effectiveStatus];
      setTip({
        bx: z.x + z.w / 2, by: z.y,
        node: (
          <>
            <div style={{ fontWeight:700, color:'white', fontSize:'12px', marginBottom:'4px' }}>{z.id} — {z.name}</div>
            <div style={{ fontSize:'11px' }}>Status: <span style={{ color: col.stroke, fontWeight:700, textTransform:'capitalize' }}>{effectiveStatus}</span></div>
            {(effectiveStatus === 'alarm' || effectiveStatus === 'tamper') && (
              <div style={{ marginTop:'4px', fontSize:'11px', color:'#ef4444', fontWeight:700 }}>⚠ Requires immediate attention</div>
            )}
            {mode === 'access' && (
              <div style={{ marginTop:'8px', display:'flex', gap:'5px' }}>
                <div onClick={ev => { ev.stopPropagation(); onZoneClick?.(z); setTip(null); setSel(null); }}
                  style={{ padding:'3px 9px', background:'rgba(245,158,11,0.12)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'5px', fontSize:'10px', color:'#f59e0b', cursor:'pointer', fontWeight:700 }}>
                  Isolate Zone
                </div>
                <div onClick={ev => { ev.stopPropagation(); onZoneClick?.(z); setTip(null); setSel(null); }}
                  style={{ padding:'3px 9px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'5px', fontSize:'10px', color:'#22c55e', cursor:'pointer', fontWeight:700 }}>
                  Reset Zone
                </div>
              </div>
            )}
          </>
        ),
      });
    } else {
      setTip(null);
    }
  }

  function pickCamera(cam: MapCamera, e: React.MouseEvent) {
    e.stopPropagation();
    const next = sel === cam.id ? null : cam.id;
    setSel(next);
    if (next) {
      setTip({
        bx: cam.cx, by: cam.cy,
        node: (
          <>
            <div style={{ fontWeight: 700, color: 'white', fontSize: '12px', marginBottom: '4px' }}>
              {cam.id} — {cam.name}
            </div>
            <div style={{ fontSize: '11px', color: '#a0a0a0', marginBottom: '2px' }}>Zone: {cam.zone}</div>
            <div style={{ fontSize: '11px' }}>
              Status: <span style={{ color: CAM_C[cam.status], fontWeight: 700, textTransform: 'capitalize' }}>{cam.status}</span>
            </div>
            {(mode === 'video' || mode === 'access') && (
              <div
                onClick={() => { onCameraClick?.(cam); setTip(null); setSel(null); }}
                style={{ marginTop: '8px', padding: '4px 10px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', fontSize: '11px', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, textAlign: 'center' }}
              >
                {mode === 'video' ? '➕ Add to video grid' : '📹 View Feed'}
              </div>
            )}
          </>
        ),
      });
      if (mode !== 'video' && mode !== 'access') onCameraClick?.(cam);
    } else {
      setTip(null);
    }
  }

  function pickDoor(door: MapDoor, e: React.MouseEvent) {
    e.stopPropagation();
    const next = sel === door.id ? null : door.id;
    setSel(next);
    if (next) {
      const effectiveStatus = (liveDoorStatus?.[door.id] ?? door.status) as MapDoor['status'];
      const statusColor = DOOR_C[effectiveStatus] ?? DOOR_C.locked;
      setTip({
        bx: door.x, by: door.y,
        node: (
          <>
            <div style={{ fontWeight: 700, color: 'white', fontSize: '12px', marginBottom: '4px' }}>
              {door.id} — {door.name}
            </div>
            <div style={{ fontSize: '11px', marginBottom: '2px' }}>
              Status: <span style={{ color: statusColor, fontWeight: 700, textTransform: 'capitalize' }}>{effectiveStatus}</span>
            </div>
            {(effectiveStatus === 'forced' || effectiveStatus === 'tamper') && (
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#ef4444', fontWeight: 700 }}>
                ⚠ Requires immediate attention
              </div>
            )}
            {(mode === 'access' || mode === 'soc') && (
              <div style={{ marginTop: '8px', display: 'flex', gap: '5px' }}>
                {(['lock','unlock','open'] as const).map(cmd => {
                  const colors: Record<string,[string,string]> = { lock:['#555555','rgba(100,116,139,0.15)'], unlock:['#22c55e','rgba(34,197,94,0.12)'], open:['#3b82f6','rgba(59,130,246,0.12)'] };
                  const [col, bg] = colors[cmd];
                  return (
                    <div key={cmd} onClick={ev => { ev.stopPropagation(); onDoorControl?.(door.id, cmd); setTip(null); setSel(null); }}
                      style={{ padding:'3px 9px', background:bg, border:`1px solid ${col}40`, borderRadius:'5px', fontSize:'10px', color:col, cursor:'pointer', fontWeight:700, textTransform:'capitalize' }}>
                      {cmd}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ),
      });
      if (mode !== 'access' && mode !== 'soc') onDoorClick?.(door);
    } else {
      setTip(null);
    }
  }

  function handleSvgMouseMove(e: React.MouseEvent) {
    if (!dragging) return;
    const { x, y } = svgCoords(e);
    const nx = Math.round(Math.max(16, Math.min(VW - 16, x - dragging.ox)));
    const ny = Math.round(Math.max(16, Math.min(VH - 16, y - dragging.oy)));
    if (dragging.type === 'door') {
      setPositions(prev => ({ ...prev, [dragging.id]: { x: nx, y: ny } }));
    } else {
      setCamPositions(prev => ({ ...prev, [dragging.id]: { x: nx, y: ny } }));
    }
    if (!dragging.moved) setDragging(d => d ? { ...d, moved: true } : d);
  }

  function handleSvgMouseUp(e: React.MouseEvent) {
    if (!dragging) return;
    const { id, type, moved } = dragging;
    setDragging(null);

    if (type === 'door') {
      const finalPos = positions[id];
      if (moved && finalPos) {
        apiSavePositions(positions, camPositions);
        onPositionChange?.(id, finalPos.x, finalPos.y);
      } else if (!moved && editLayout) {
        const door = doors.find(d => d.id === id);
        if (door) {
          const pos = getDoorPos(door);
          const c = DOOR_C[door.status];
          setSel(id);
          setTip({
            bx: pos.x, by: pos.y,
            node: (
              <>
                <div style={{ fontWeight: 700, color: 'white', fontSize: '12px', marginBottom: '6px' }}>{door.name}</div>
                <div style={{ fontSize: '11px', marginBottom: '8px' }}>
                  Status: <span style={{ color: c, fontWeight: 700, textTransform: 'capitalize' }}>{door.status}</span>
                </div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); onRemoveDoor?.(door.id); setSel(null); setTip(null); }}
                  style={{ width: '100%', padding: '5px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '6px', color: '#ef4444', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                >Remove from map</button>
              </>
            ),
          });
        }
      }
    } else {
      // Camera
      const finalPos = camPositions[id];
      if (moved && finalPos) {
        apiSavePositions(positions, camPositions);
        onCameraPositionChange?.(id, finalPos.x, finalPos.y);
      } else if (!moved && editLayout) {
        const cam = cameras.find(c => c.id === id);
        if (cam) {
          const pos = getCamPos(cam);
          setSel(id);
          setTip({
            bx: pos.x, by: pos.y,
            node: (
              <>
                <div style={{ fontWeight: 700, color: 'white', fontSize: '12px', marginBottom: '6px' }}>{cam.name}</div>
                <div style={{ fontSize: '11px', marginBottom: '8px', color: '#a0a0a0' }}>{cam.id}</div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); onRemoveCamera?.(cam.id); setSel(null); setTip(null); }}
                  style={{ width: '100%', padding: '5px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '6px', color: '#ef4444', fontSize: '11px', cursor: 'pointer', fontWeight: 600 }}
                >Remove from map</button>
              </>
            ),
          });
        }
      }
    }
  }

  return (
    <div style={{ position: 'relative' }} onClick={() => { if (!dragging) { setSel(null); setTip(null); } }}>
      {/* Edit layout banner */}
      {editLayout && (
        <div style={{ marginBottom: '8px', padding: '6px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px' }}>✏️</span>
          <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 600 }}>Layout Edit Mode</span>
          <span style={{ fontSize: '11px', color: '#a0a0a0' }}>— Drag doors to reposition them. Positions are saved automatically.</span>
          <button
            onClick={() => { setPositions({}); localStorage.removeItem('siteMapDoorPositions'); }}
            style={{ marginLeft: 'auto', padding: '3px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '6px', color: '#ef4444', fontSize: '10px', cursor: 'pointer', fontWeight: 600 }}
          >Reset positions</button>
        </div>
      )}
      {/* Legend */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: '#444444', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Legend:</span>
        {mode === 'soc' && <>
          <LI color="#22c55e" label="Camera online" />
          <LI color="#f59e0b" label="Motion" />
          <LI color="#ef4444" label="Alarm / offline" />
          {alarms.length > 0 && <LI color="#ef4444" label="Active alarm zone" rect />}
        </>}
        {mode === 'video' && <>
          <LI color="#22c55e" label="Online" />
          <LI color="#f59e0b" label="Motion" />
          <LI color="#ef4444" label="Offline" />
          <span style={{ fontSize: '10px', color: '#3b82f6', marginLeft: '4px' }}>Click a camera to add to grid</span>
        </>}
        {mode === 'access' && <>
          <LI color="#22c55e" label="Door unlocked" />
          <LI color="#555555" label="Door locked" />
          <LI color="#ef4444" label="Forced / tamper" />
          <LI color="#3b82f6" label="Door open" />
          <LI color="#f59e0b" label="Output active" diamond />
          <LI color="#222222" label="Output inactive" diamond />
          <LI color="rgba(59,130,246,0.5)" label="Alarm zone" rect />
          <LI color="#ef4444" label="Zone alarm" rect />
        </>}
        {mode === 'vision' && <>
          <LI color="rgba(59,130,246,0.8)" label="Low density" />
          <LI color="rgba(245,158,11,0.8)" label="Medium density" />
          <LI color="rgba(239,68,68,0.8)" label="High density" />
          <span style={{ fontSize: '10px', color: '#555555', marginLeft: '4px' }}>Live zone occupancy overlay</span>
        </>}
      </div>

      <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '5px', overflow: 'hidden', position: 'relative' }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VW} ${VH}`}
          style={{ width: '100%', height: height, display: 'block', cursor: dragging ? 'grabbing' : 'default' }}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={e => handleSvgMouseUp(e)}
          onMouseLeave={e => handleSvgMouseUp(e)}
        >
          {/* Background */}
          <rect width={VW} height={VH} fill={bgImage ? '#000' : '#060b16'} />

          {/* Custom floor plan image */}
          {bgImage && (
            <image href={bgImage} x={0} y={0} width={VW} height={VH} preserveAspectRatio="xMidYMid meet" opacity={0.85} />
          )}

          {/* Grid (hidden when custom image used) */}
          {!bgImage && Array.from({ length: 23 }, (_, i) => (
            <line key={`vg${i}`} x1={i * 36} y1={0} x2={i * 36} y2={VH} stroke="rgba(255,255,255,0.016)" strokeWidth={1} />
          ))}
          {!bgImage && Array.from({ length: 14 }, (_, i) => (
            <line key={`hg${i}`} x1={0} y1={i * 36} x2={VW} y2={i * 36} stroke="rgba(255,255,255,0.016)" strokeWidth={1} />
          ))}

          {/* Zone fills — hidden when custom image is used and showZones=false */}
          {(showZones || !bgImage) && BLDG.map(z => {
            const isExt = z.id === 'carpark';
            const hasAlarm = alarmZoneIds.has(z.id);
            const fill = mode === 'vision'
              ? zoneHeat(z.occupancy)
              : hasAlarm && mode === 'soc'
                ? 'rgba(239,68,68,0.07)'
                : isExt
                  ? 'rgba(255,255,255,0.010)'
                  : 'rgba(255,255,255,0.020)';
            const strokeC = hasAlarm && mode === 'soc' ? '#ef4444' : isExt ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.09)';
            return (
              <g key={z.id}>
                <rect
                  x={z.x} y={z.y} width={z.w} height={z.h}
                  fill={fill}
                  stroke={strokeC}
                  strokeWidth={isExt ? 1 : 1.5}
                  strokeDasharray={isExt ? '7,4' : undefined}
                  rx={4}
                />
                {/* Pulsing alarm outline */}
                {hasAlarm && mode === 'soc' && (
                  <rect
                    x={z.x + 1.5} y={z.y + 1.5} width={z.w - 3} height={z.h - 3}
                    fill="none" stroke="#ef4444" strokeWidth={2}
                    strokeDasharray="8,5" opacity={0.55} rx={3}
                  />
                )}
                {/* Zone label */}
                <text
                  x={z.x + z.w / 2} y={z.y + (z.sub ? 17 : 14)}
                  textAnchor="middle" fill="rgba(255,255,255,0.28)"
                  fontSize={z.w < 130 ? 9 : 11}
                  fontFamily="system-ui,sans-serif" fontWeight="600"
                >
                  {z.label}
                </text>
                {z.sub && (
                  <text
                    x={z.x + z.w / 2} y={z.y + 31}
                    textAnchor="middle" fill="rgba(255,255,255,0.15)"
                    fontSize={9} fontFamily="system-ui,sans-serif"
                  >
                    {z.sub}
                  </text>
                )}
                {/* Vision: occupancy badge */}
                {mode === 'vision' && (
                  <g>
                    <rect
                      x={z.x + z.w / 2 - 20} y={z.y + z.h - 26}
                      width={40} height={18} rx={5}
                      fill="rgba(0,0,0,0.65)" stroke="rgba(255,255,255,0.12)" strokeWidth={1}
                    />
                    <text
                      x={z.x + z.w / 2} y={z.y + z.h - 13}
                      textAnchor="middle"
                      fill={z.occupancy > 65 ? '#ef4444' : z.occupancy > 40 ? '#f59e0b' : '#22c55e'}
                      fontSize={10} fontFamily="system-ui" fontWeight="700"
                    >
                      {z.occupancy}%
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Alarm zones (access mode) — drawn under doors/cameras */}
          {mode === 'access' && alarmZones.map(z => {
            const effectiveStatus = (liveZoneStatus?.[z.id] ?? z.status) as MapZone['status'];
            const col = ZONE_C[effectiveStatus];
            const isSel = sel === z.id;
            return (
              <g key={z.id} onClick={e => pickAlarmZone(z, e)} style={{ cursor:'pointer' }}>
                <rect x={z.x} y={z.y} width={z.w} height={z.h}
                  fill={col.fill} stroke={isSel ? col.stroke : col.stroke}
                  strokeWidth={isSel ? 2 : 1} strokeDasharray="6,3" rx={6} opacity={0.85} />
                {/* Zone label top-left */}
                <text x={z.x + 8} y={z.y + 14} fontSize={8} fill={col.stroke}
                  fontFamily="system-ui,sans-serif" fontWeight="700" opacity={0.9}>
                  {z.name}
                </text>
                {/* Status badge */}
                {effectiveStatus !== 'normal' && (
                  <g>
                    <rect x={z.x + z.w - 54} y={z.y + 4} width={50} height={14} rx={4}
                      fill={effectiveStatus === 'alarm' || effectiveStatus === 'tamper' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.15)'} />
                    <text x={z.x + z.w - 29} y={z.y + 14} textAnchor="middle" fontSize={7.5}
                      fill={effectiveStatus === 'alarm' || effectiveStatus === 'tamper' ? '#ef4444' : '#f59e0b'}
                      fontFamily="system-ui" fontWeight="700">
                      {effectiveStatus.toUpperCase()}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Door elements (SOC + access modes) */}
          {(mode === 'access' || mode === 'soc') && doors.map(door => {
            const pos = getDoorPos(door);
            const effectiveStatus = (liveDoorStatus?.[door.id] ?? door.status) as MapDoor['status'];
            const c = DOOR_C[effectiveStatus] ?? DOOR_C.locked;
            const isSel = sel === door.id;
            const isDragged = dragging?.id === door.id;
            const isAlert = effectiveStatus === 'forced' || effectiveStatus === 'tamper';
            const shortName = door.name.length > 14 ? door.name.slice(0, 13) + '…' : door.name;
            const px = pos.x, py = pos.y;
            return (
              <g key={door.id}
                onClick={e => { if (!editLayout) pickDoor(door, e); else e.stopPropagation(); }}
                onMouseDown={editLayout ? (e => {
                  e.stopPropagation();
                  const { x, y } = svgCoords(e);
                  setDragging({ id: door.id, type: 'door', ox: x - px, oy: y - py, moved: false });
                }) : undefined}
                style={{ cursor: editLayout ? (isDragged ? 'grabbing' : 'grab') : 'pointer' }}
              >
                {/* Alert pulse ring */}
                {isAlert && (
                  <circle cx={px} cy={py} r={22} fill="none" stroke={c} strokeWidth={1.5} opacity={0.25} />
                )}
                {/* Selection ring */}
                {isSel && (
                  <circle cx={px} cy={py} r={20} fill="none" stroke={c} strokeWidth={2} opacity={0.6} />
                )}
                {/* Edit mode: drag outline */}
                {editLayout && (
                  <rect x={px - 16} y={py - 18} width={32} height={36} fill="rgba(245,158,11,0.05)" stroke={isDragged ? '#f59e0b' : 'rgba(245,158,11,0.35)'} strokeWidth={1} strokeDasharray={isDragged ? undefined : '5,3'} rx={5} />
                )}
                {/* Door body: dark card */}
                <rect x={px - 13} y={py - 15} width={26} height={30}
                  fill={isDragged ? 'rgba(245,158,11,0.25)' : 'rgba(8,14,26,0.88)'}
                  stroke={isSel ? c : isDragged ? '#f59e0b' : `${c}88`}
                  strokeWidth={isSel ? 2 : 1.5} rx={4}
                />
                {/* Status strip (top bar) */}
                <rect x={px - 13} y={py - 15} width={26} height={7} fill={c} opacity={0.9} rx={4} />
                <rect x={px - 13} y={py - 11} width={26} height={3} fill={c} opacity={0.9} />
                {/* Door panel icon lines */}
                <line x1={px - 6} y1={py - 4} x2={px - 6} y2={py + 10} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} strokeLinecap="round" />
                <line x1={px - 6} y1={py - 4} x2={px + 7} y2={py - 4} stroke="rgba(255,255,255,0.35)" strokeWidth={1.5} strokeLinecap="round" />
                {/* Door handle */}
                <circle cx={px + 4} cy={py + 3} r={1.8} fill="rgba(255,255,255,0.7)" />
                {/* Name label below card */}
                <text x={px} y={py + 22} textAnchor="middle" fontSize={7.5} fill={isSel ? c : 'rgba(255,255,255,0.55)'} fontFamily="system-ui,sans-serif" fontWeight={isSel ? '700' : '500'}>
                  {shortName}
                </text>
              </g>
            );
          })}

          {/* Camera elements */}
          {cameras.map(cam => {
            const c = CAM_C[cam.status];
            const isSel = sel === cam.id;
            const isDraggedCam = dragging?.id === cam.id && dragging?.type === 'camera';
            const r = isSel ? 12 : 10;
            const cp = getCamPos(cam);
            return (
              <g key={cam.id}
                onClick={e => { if (!editLayout) pickCamera(cam, e); else e.stopPropagation(); }}
                onMouseDown={editLayout ? (e => {
                  e.stopPropagation();
                  const { x, y } = svgCoords(e);
                  setDragging({ id: cam.id, type: 'camera', ox: x - cp.x, oy: y - cp.y, moved: false });
                }) : undefined}
                style={{ cursor: editLayout ? (isDraggedCam ? 'grabbing' : 'grab') : 'pointer' }}
              >
                {/* Edit mode drag outline */}
                {editLayout && (
                  <circle cx={cp.x} cy={cp.y} r={r + 6}
                    fill="rgba(245,158,11,0.05)" stroke={isDraggedCam ? '#f59e0b' : 'rgba(245,158,11,0.35)'}
                    strokeWidth={1} strokeDasharray={isDraggedCam ? undefined : '5,3'} />
                )}
                {/* Coverage cone (video mode only) */}
                {mode === 'video' && cam.status !== 'offline' && (
                  <ellipse
                    cx={cp.x} cy={cp.y + 30} rx={28} ry={18}
                    fill={`${c}10`} stroke={`${c}22`} strokeWidth={1}
                  />
                )}
                {/* Pulse ring for active events */}
                {(cam.status === 'alarm' || cam.status === 'motion') && (
                  <circle cx={cp.x} cy={cp.y} r={r + 8}
                    fill="none" stroke={c} strokeWidth={1} opacity={0.22} />
                )}
                {/* Camera body */}
                <circle
                  cx={cp.x} cy={cp.y} r={r}
                  fill={isDraggedCam ? `${c}44` : `${c}1e`} stroke={c} strokeWidth={isSel ? 2.5 : 1.5}
                />
                {/* Inner dot */}
                <circle cx={cp.x} cy={cp.y} r={3} fill={c} />
                {/* Name label */}
                <text x={cp.x} y={cp.y + r + 10} textAnchor="middle" fontSize={7.5}
                  fill={isSel ? c : 'rgba(255,255,255,0.45)'} fontFamily="system-ui,sans-serif" fontWeight={isSel ? '700' : '500'}>
                  {cam.name.length > 12 ? cam.name.slice(0,11)+'...' : cam.name}
                </text>
                {/* ID label when selected */}
                {isSel && !editLayout && (
                  <g>
                    <rect
                      x={cp.x - 38} y={cp.y + r + 14} width={76} height={15}
                      fill="rgba(0,0,0,0.90)" stroke={`${c}45`} strokeWidth={1} rx={4}
                    />
                    <text
                      x={cp.x} y={cp.y + r + 25}
                      textAnchor="middle" fill={c}
                      fontSize={8} fontFamily="system-ui" fontWeight="700"
                    >
                      {cam.id}
                    </text>
                  </g>
                )}
              </g>
            );
          })}

          {/* Output elements (access mode) */}
          {mode === 'access' && outputs.map(out => {
            const c = OUT_C[out.status];
            const isSel = sel === out.id;
            return (
              <g key={out.id} onClick={e => pickOutput(out, e)} style={{ cursor:'pointer' }}>
                {/* Pulse ring for active outputs */}
                {out.status === 'active' && (
                  <circle cx={out.cx} cy={out.cy} r={16} fill="none" stroke={c} strokeWidth={1} opacity={0.25} />
                )}
                {/* Diamond shape for outputs */}
                <polygon
                  points={`${out.cx},${out.cy - 10} ${out.cx + 9},${out.cy} ${out.cx},${out.cy + 10} ${out.cx - 9},${out.cy}`}
                  fill={isSel ? `${c}30` : `${c}18`} stroke={c} strokeWidth={isSel ? 2 : 1.5}
                />
                <circle cx={out.cx} cy={out.cy} r={3} fill={c} />
                <text x={out.cx} y={out.cy + 20} textAnchor="middle" fontSize={7.5}
                  fill={isSel ? c : 'rgba(255,255,255,0.4)'} fontFamily="system-ui,sans-serif" fontWeight={isSel ? '700' : '500'}>
                  {out.name.length > 10 ? out.name.slice(0,9)+'…' : out.name}
                </text>
              </g>
            );
          })}

          {/* Alarm markers on map (SOC mode) */}
          {mode === 'soc' && alarms.map(alarm => {
            const zone = BLDG.find(z => z.id === alarm.zone);
            if (!zone) return null;
            const cx = zone.x + zone.w * 0.76;
            const cy = zone.y + zone.h * 0.24;
            const c = SEV_C[alarm.severity] || '#555555';
            return (
              <g key={`al-${alarm.id}`}>
                <circle cx={cx} cy={cy} r={19} fill={`${c}20`} stroke={c} strokeWidth={1.5} opacity={0.35} />
                <circle cx={cx} cy={cy} r={13} fill={`${c}28`} stroke={c} strokeWidth={2} />
                <text x={cx} y={cy + 5} textAnchor="middle" fontSize={11}>🚨</text>
              </g>
            );
          })}

          {/* North indicator */}
          <g>
            <circle cx={VW - 30} cy={54} r={18} fill="rgba(0,0,0,0.55)" stroke="rgba(255,255,255,0.10)" strokeWidth={1} />
            <text x={VW - 30} y={45} textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.50)"
              fontWeight="700" fontFamily="system-ui">N</text>
            <line x1={VW - 30} y1={47} x2={VW - 30} y2={65}
              stroke="rgba(255,255,255,0.30)" strokeWidth={1.5} />
            <polygon
              points={`${VW - 30},47 ${VW - 34},59 ${VW - 26},59`}
              fill="rgba(255,255,255,0.40)" />
          </g>

          {/* Floor label */}
          <text
            x={VW - 14} y={VH - 10}
            textAnchor="end" fontSize={9}
            fill="rgba(255,255,255,0.10)" fontFamily="system-ui" fontWeight="600"
          >
            Town Hall — Level 1
          </text>
        </svg>

        {/* Floating tooltip */}
        {tip && (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: `${Math.min((tip.bx / VW) * 100, 72)}%`,
              top: Math.max(8, (tip.by / VH) * height - 105),
              background: 'rgba(13,19,33,0.97)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '4px',
              padding: '10px 14px',
              zIndex: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.65)',
              minWidth: '160px',
              maxWidth: '220px',
            }}
          >
            {tip.node}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Legend item helper ───────────────────────────────────────────────────────

function LI({ color, label, rect, diamond }: { color: string; label: string; rect?: boolean; diamond?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      {rect
        ? <div style={{ width: '12px', height: '8px', borderRadius: '2px', border: `1.5px solid ${color}`, background: `${color}25`, flexShrink: 0 }} />
        : diamond
          ? <div style={{ width: '9px', height: '9px', background: color, flexShrink: 0, transform: 'rotate(45deg)', borderRadius: '1px' }} />
          : <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
      }
      <span style={{ fontSize: '10px', color: '#555555' }}>{label}</span>
    </div>
  );
}
